import { IcoNotification } from "assets/icons";
import { ActionButton, Button, PannelCard, TravelCard } from "components";

import EmptyState from "assets/images/img-empty-state.svg";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPartnerUuid } from "services/auth";
import { getPartner } from "services/partner";
import { getTripRequests } from "services/trip-request";
import { RootState } from "store";
import styles from "./styles.module.scss";

type Trip = {
	id: string;
	status: string;
	departure: string;
	arrival: string;
	departureDate: string;
	arrivalDate: string;
	numberPassengers: number;
};

const monthNames = [
	"Janeiro",
	"Fevereiro",
	"Março",
	"Abril",
	"Maio",
	"Junho",
	"Julho",
	"Agosto",
	"Setembro",
	"Outubro",
	"Novembro",
	"Dezembro"
];

export default function HomePage() {
	const navigate = useNavigate();

	const { logged_user } = useSelector((state: RootState) => state.tokens);

	const [partnerId, setPartnerId] = useState("");
	const [name, setName] = useState("");
	const [currentMonthName, setCurrentMonthName] = useState("");
	const [currentMonthTripsCount, setCurrentMonthTripsCount] = useState(0);
	const [currentMonthRevenue, setCurrentMonthRevenue] = useState(0);
	const [pendingTrips, setPendingTrips] = useState<Trip[]>([]);
	const [nextTrip, setNextTrip] = useState<Trip | null>(null);

	const fetchData = async () => {
		if (!logged_user) return;

		const now = new Date();
		const currentMonth = now.getMonth();
		const currentYear = now.getFullYear();

		setCurrentMonthName(monthNames[currentMonth]);

		const partnerUuid = (await getPartnerUuid()) || "";
		setPartnerId(partnerUuid);

		const partner: any = await getPartner(partnerUuid);
		setName(partner.name);

		const allTrips = (await getTripRequests()) ?? [];

		allTrips.sort((a: any, b: any) => {
			const dateA = new Date(a.created_at).getTime();
			const dateB = new Date(b.created_at).getTime();
			return dateB - dateA;
		});

		const apiTripRequests = allTrips
			.map((trip: any) => ({
				...trip,
				origin: trip.points.find((point: any) => point.type === "origin"),
				destination: trip.points.find(
					(point: any) => point.type === "destination"
				)
			}))
			.filter((trip: any) => trip.origin && trip.destination)
			.map((trip: any) => ({
				id: trip.id,
				status: trip.status,
				departure: trip.origin.city,
				arrival: trip.destination.city,
				departureDate: new Date(trip.origin.datetime).toLocaleDateString(
					"pt-BR"
				),
				arrivalDate: new Date(
					trip.destination.datetime || trip.origin.datetime
				).toLocaleDateString("pt-BR"),
				numberPassengers: trip.num_adults + trip.num_children + trip.num_pets,
				originalDate: new Date(trip.origin.datetime),
				estimatedCost: parseFloat(trip.estimated_cost)
			}));

		setPendingTrips(
			apiTripRequests
				.filter((trip: any) => trip.status === "pending")
				.slice(0, 5)
		);

		const paidTrips = apiTripRequests.filter(
			(trip: any) => trip.status === "paid"
		);

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		setNextTrip(
			paidTrips.filter((trip: any) => trip.originalDate >= today)[0] || null
		);

		const paidTripsThisMonth = paidTrips.filter(
			(trip: any) =>
				trip.originalDate.getMonth() === currentMonth &&
				trip.originalDate.getFullYear() === currentYear
		);
		setCurrentMonthTripsCount(paidTripsThisMonth.length);
	};

	useEffect(() => {
		fetchData();
	}, []);

	return (
		<div className={styles.container}>
			<div className={styles.homeHeader}>
				<span>Olá, {name}</span>
				<ActionButton icon={<IcoNotification />} text="" onClick={() => {}} />
			</div>
			<div className={styles.homeContent}>
				<div className={styles.pannelSection}>
					<span>Painel</span>
					<PannelCard
						month={currentMonthName}
						totalTravels={`${currentMonthTripsCount} viage${
							currentMonthTripsCount !== 1 ? "ns" : "m"
						}`}
						totalRevenue={`R$ ${currentMonthRevenue
							.toFixed(2)
							.replace(".", ",")}`}
					/>
				</div>
				{!nextTrip && pendingTrips.length === 0 && (
					<div className={styles.travelEmptyState}>
						<img src={EmptyState} alt="Nenhuma viagem encontrada" />
						<h2>Pronto para a primeira viagem!</h2>
						<span>Responda rápido e conquiste seus clientes</span>
					</div>
				)}
				<div className={styles.contentSection}>
					{nextTrip && (
						<>
							<span>Próxima viagem</span>
							<span>Sua próxima viagem confirmada é:</span>
							<TravelCard
								departure={nextTrip.departure}
								arrival={nextTrip.arrival}
								departureDate={nextTrip.departureDate}
								arrivalDate={nextTrip.arrivalDate}
								passengers={nextTrip.numberPassengers}
								onClick={() => navigate(`/viagens/${nextTrip.id}/agendada`)}
							/>
						</>
					)}
				</div>
				<div className={styles.contentSection}>
					{pendingTrips.length > 0 && (
						<>
							<span>Propostas</span>
							<span>Você recebeu novas propostas de viagem:</span>
							{pendingTrips.map((trip) => (
								<TravelCard
									departure={trip.departure}
									arrival={trip.arrival}
									departureDate={trip.departureDate}
									arrivalDate={trip.arrivalDate}
									passengers={trip.numberPassengers}
									onClick={() => navigate(`/viagens/${trip.id}/nova-viagem`)}
								/>
							))}
							<Button
								onClick={() => navigate("/viagens")}
								className={styles.seeMoreButton}
							>
								Veja mais viagens
							</Button>
						</>
					)}
				</div>
				{/*
				<div className={styles.publicity}>
					<span>publicidade</span>
					<img src={PublicityTemplate} alt="" />
				</div>
				*/}
			</div>
		</div>
	);
}
